import React, { useEffect, useRef } from 'react';
import { Box, Paper, Typography, LinearProgress, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Poll } from '@mui/icons-material';
import { useGetPoll } from '../../services/stagemode_api/talent/talent_api';
import SocketHandler from '../../utils/socketHandler';

const PollContainer = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  right: '20px',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  backdropFilter: 'blur(10px)',
  color: 'white',
  maxWidth: '300px',
  zIndex: 1000,
}));


interface AudiencePollDisplayProps {
  bookingId: string;
}

const AudiencePollDisplay: React.FC<AudiencePollDisplayProps> = ({ bookingId }) => {
  const socketSubscribedRef = useRef(false);
  // Add poll data fetching
  const { data: polls, refetch: refetchPoll } = useGetPoll(bookingId);
  const activePoll = polls?.data?.find((poll) => poll.status === "Open");

  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current) {
      return;
    }

    // Get socket instance and subscribe to messages
    const socketHandler = SocketHandler.getInstance();

    socketHandler.subscribeToBooking(bookingId, (message) => {

      if (message?.type === "PollClosed" || message?.type === "Poll" || message?.type === "PollVote") {
        refetchPoll();
      }
    });

    socketSubscribedRef.current = true;

    // Cleanup subscription when component unmounts
    return () => {
      socketHandler.unsubscribeFromBooking(bookingId);
      socketSubscribedRef.current = false;
    };
  }, [bookingId, refetchPoll]);

  if (!activePoll || activePoll.status !== 'Open') return null;

  const totalVotes = activePoll?.options?.reduce((sum, opt) => sum + opt.votes, 0);

  return (

    <PollContainer elevation={3}>
      <Box display="flex" alignItems="center" minWidth={300} mb={2}>
        <Poll sx={{ mr: 1 }} />
        <Typography variant="h6" fontWeight={600}>
          Live Poll
        </Typography>
      </Box>
      <Typography variant="subtitle1" mb={2}>
        {activePoll.title}
      </Typography>
      <Stack spacing={2}>
      <Typography variant="body2">Total: {totalVotes}</Typography>
        {activePoll?.options?.map((option) => (
          <Box key={option.id}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">{option.option}</Typography>
              <Typography variant="body2">
                {option.votes} 
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={option.votes ? (option.votes / totalVotes) * 100 : 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: '#4CAF50',
                },
              }}
            />
          </Box>
        ))}
      </Stack>
    </PollContainer>
  );
};

export default AudiencePollDisplay; 